import classNames from 'classnames';
import { ForwardedRef, forwardRef, ReactNode, MouseEventHandler } from 'react';
import { Link } from 'wouter';

import styles from '../../styles/buttons/basic-button.module.css';

export type BasicButtonElement = HTMLAnchorElement | HTMLButtonElement | HTMLLinkElement;

export interface BasicButtonProps {
    type?: 'button';
    href?: string | null;
    external?: boolean;
    target?: string | null;
    download?: string | null;
    disabled?: boolean;
    children?: ReactNode;
    className?: string | null;
    onClick?: MouseEventHandler<BasicButtonElement> | null;
    buttonRef?: ForwardedRef<BasicButtonElement> | null;
}

function BasicButtonBase({
    type = 'button',
    href = null,
    external = false,
    target = null,
    download = null,
    children = null,
    disabled = false,
    className = null,
    onClick = null,
    buttonRef = null,
}: BasicButtonProps) {
    if (href !== null && !external) {
        return (
            <Link
                href={href}
                onClick={onClick || undefined}
                ref={buttonRef}
                className={classNames([
                    styles.container,
                    {
                        [className || '']: className !== null,
                    },
                ])}
            >
                {children}
            </Link>
        );
    }

    const ButtonComponent = href !== null ? 'a' : 'button';

    return (
        <ButtonComponent
            type={ButtonComponent === 'button' ? type : undefined}
            href={href || undefined}
            target={target === null && external ? '_blank' : target || undefined}
            className={classNames([
                styles.container,
                {
                    [className || '']: className !== null,
                },
            ])}
            disabled={disabled}
            onClick={onClick || undefined}
            download={download || undefined}
            ref={buttonRef}
        >
            {children}
        </ButtonComponent>
    );
}

export default forwardRef<BasicButtonElement, Omit<BasicButtonProps, 'buttonRef'>>(
    function BasicButton(props, ref) {
        return <BasicButtonBase {...props} buttonRef={ref} />;
    },
);
